import { getQueryClient } from "@/app/react-query/query-client"
import type { ItemOffer } from "@anaconda/core/bid/types"
import type { NftId } from "@anaconda/core/nft/types"

/**
 * The query is keyed by the id from the URL, which keeps whatever casing the
 * link used (a checksummed EVM address), while callers pass the BFF's
 * lowercase id — so match ids case-insensitively rather than by key equality.
 */
function isOffersQueryFor(id: NftId) {
  const target = id.toLowerCase()

  return ({
    queryKey: [scope, type, input],
  }: { queryKey: readonly unknown[] }) =>
    scope === "nfts" &&
    type === "offers" &&
    typeof input === "object" &&
    input !== null &&
    "id" in input &&
    typeof input.id === "string" &&
    input.id.toLowerCase() === target
}

/**
 * The item Offers tab has no refetch interval, so a filled, placed or
 * cancelled bid only shows once its query is invalidated.
 */
export function invalidateNftOffersQueries(id: NftId) {
  void getQueryClient().invalidateQueries({ predicate: isOffersQueryFor(id) })
}

type OffersPages = { pages: { offers: ItemOffer[] }[] }

/**
 * Takes a filled OpenSea offer off the item's Offers tab straight away. A
 * refetch alone can bring it back: OpenSea keeps listing an order for a short
 * while after it is filled.
 */
export function removeFilledOpenSeaOffer(id: NftId, orderHash: string) {
  const filled = orderHash.toLowerCase()

  getQueryClient().setQueriesData<OffersPages>(
    { predicate: isOffersQueryFor(id) },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          offers: page.offers.filter(
            (offer) => offer.openSea?.orderHash.toLowerCase() !== filled,
          ),
        })),
      },
  )
}
